import type {CPCPlacementKey} from '@/config/models';
import type {ExtractPropsWithoutRef} from '@/types';

import {useBreakpointDown, useCurrentBreakpoint} from '@travel-frontend/core';
import {AdvertisingCPC} from '@travel-frontend/ui-components';

import {config} from '@/config';
import {PAGE_HOTELS} from '@/consts';
import {useLocalSplit} from '@/modules/common/hooks';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';

const handleAdsLoaded = (adElementId: string) => {
  const adIframe = document.getElementById(adElementId)?.firstElementChild;
  if (adIframe instanceof HTMLIFrameElement) {
    // the ads don't handle keyboard focus well, so we take them out of the tab flow completely
    adIframe.setAttribute('tabIndex', '-1');
  }
};

type Props = Omit<
  ExtractPropsWithoutRef<typeof AdvertisingCPC>,
  'placementKey' | 'id'
> & {id: CPCPlacementKey};

export const CpcAd: React.FunctionComponent<Props> = ({
  id,
  query,
  ...restProps
}) => {
  const phone = useBreakpointDown('sm');
  const tablet = useCurrentBreakpoint() === 'md';
  const currentPage = useCurrentPage();
  const hotelsCpcSplitEnabled =
    useLocalSplit('FFP_3768_hotels_cpc_ads').treatment === 'on';
  const offersCpcSplitEnabled =
    useLocalSplit('FFP_3768_offers_cpc_ads').treatment === 'on';

  const enabled =
    currentPage === PAGE_HOTELS ? hotelsCpcSplitEnabled : offersCpcSplitEnabled;

  let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  if (phone) {
    deviceType = 'mobile';
  } else if (tablet) {
    deviceType = 'tablet';
  }

  const placementKey = config.cpcAds?.placementKeys[id][deviceType];

  if (!enabled || !id || !placementKey || !query) {
    return null;
  }

  return (
    <AdvertisingCPC
      {...restProps}
      id={id}
      placementKey={placementKey}
      query={query}
      adTest={import.meta.env.MODE !== 'production'}
      onAds={handleAdsLoaded}
    />
  );
};
